import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "../api/client";
import { resendVerification } from "../api/auth";
import { useAuth } from "../auth/AuthContext";

export default function UnverifiedEmailBanner() {
  const { user, refreshMe } = useAuth();
  const { t } = useTranslation("common");
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState<{ ok: boolean; text: string } | null>(null);

  if (!user || user.email_verified) return null;

  async function handleResend() {
    setSending(true);
    setFeedback(null);
    try {
      await resendVerification();
      setFeedback({ ok: true, text: t("verifyBanner.sent") });
      await refreshMe();
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === "ALREADY_VERIFIED") {
          setFeedback({ ok: true, text: t("verifyBanner.alreadyVerified") });
          await refreshMe();
        } else if (err.code === "RESEND_RATE_LIMITED") {
          setFeedback({ ok: false, text: t("verifyBanner.rateLimited") });
        } else {
          setFeedback({ ok: false, text: err.message || t("verifyBanner.failed") });
        }
      } else {
        setFeedback({ ok: false, text: t("verifyBanner.networkError") });
      }
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="border-b border-yellow-300 bg-yellow-50 px-4 py-2.5">
      <div className="mx-auto flex max-w-6xl flex-wrap items-center gap-x-3 gap-y-1 text-sm text-yellow-800">
        <svg
          className="h-4 w-4 shrink-0 text-yellow-600"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
          />
        </svg>
        <span className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
          {t("verifyBanner.message")}
          <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-700 ring-1 ring-inset ring-green-600/20">
            {t("verifyBanner.bonusPill")}
          </span>
          {t("verifyBanner.checkInbox")}{" "}
          <button
            type="button"
            onClick={handleResend}
            disabled={sending}
            className="font-semibold underline hover:no-underline disabled:opacity-50"
          >
            {sending ? t("verifyBanner.sending") : t("verifyBanner.resend")}
          </button>
        </span>
        {feedback && (
          <span
            className={
              feedback.ok
                ? "font-medium text-green-700"
                : "font-medium text-red-700"
            }
          >
            {feedback.text}
          </span>
        )}
      </div>
    </div>
  );
}
